import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { SkeletonBody } from '../Bookings/BookingDetails/SectionSkeleton';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ClientSavedCardsSection from './ClientSavedCardsSection';
import DeleteWalletEntryModal from './DeleteWalletEntryModal';
import { useZohoOrgId, zohoPaymentUrl } from '../../../utils/zohoOrg';
import './wallet.css';

// Reference-number chip — compact monospace pill for the TOP-<id> reference.
const REF_CHIP = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
    color: '#475569', background: '#eef2f7',
    padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
};

/**
 * The client's wallet — balances, deposits, transactions.
 *
 * Renders two ways since the standalone /wallet page folded into Clients
 * (owner, 2026-09-02): as its own page (route param, back link, page title)
 * and EMBEDDED inside Client Details (clientId prop, chrome suppressed, a
 * plain section heading instead). One component, one set of fetches — the
 * embed is not a copy that can drift.
 */
// onInvoicesChanged: the embedding page's own refetch (the client page's Zoho
// Billing Summary) — called after the office applies the wallet, so the
// pending list above this section is never a second behind (2026-09-10).
// `refreshToken`: the client page bumps it when it moves money itself (a
// Payment Received whose excess became wallet cash), so this panel re-reads
// without a remount.
export default function WalletClientDetails({ clientId: clientIdProp, embedded = false, onInvoicesChanged = null, refreshToken = 0, walletLabel = null, titleExtra = null }) {
    const params = useParams();
    const clientId = clientIdProp || params.clientId;
    const zohoOrgId = useZohoOrgId();
    const [client, setClient] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [pending, setPending] = useState(null);
    // "Apply wallet" — put the client's wallet cash towards their pending
    // invoices from here, oldest first (owner, 2026-09-10), instead of asking
    // the client to log in and pay from their wallet themselves.
    const [applyOpen, setApplyOpen] = useState(false);
    const [applyPreview, setApplyPreview] = useState(null);
    const [applyAmount, setApplyAmount] = useState('');
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyBusy, setApplyBusy] = useState(false);
    const [applyError, setApplyError] = useState('');
    const [loading, setLoading] = useState(true);

    const [tab, setTab] = useState('transactions');

    const [transactions, setTransactions] = useState([]);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsTotal, setTransactionsTotal] = useState(0);

    const [deposits, setDeposits] = useState([]);
    const [depositsLoading, setDepositsLoading] = useState(false);

    // Adjustment form
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustDescription, setAdjustDescription] = useState('');
    const [adjustDirection, setAdjustDirection] = useState('credit');
    const [adjustBalanceType, setAdjustBalanceType] = useState('cash');
    const [adjustSyncZoho, setAdjustSyncZoho] = useState(false);
    // When the money actually arrived — bank transfers get recorded days
    // late, and the Zoho payment must carry the received date (2026-09-02).
    const todayStr = () => new Date().toISOString().split('T')[0];
    const [adjustZohoDate, setAdjustZohoDate] = useState(todayStr());
    // Compensation (credit note) mode — damage / goodwill credit papered as a
    // tax credit note on the related invoice (owner approved 2026-09-02).
    // Top-ups page — sliced client-side (the endpoint returns everything);
    // same 6-per-page rhythm as the transactions when embedded.
    const [depositsPage, setDepositsPage] = useState(1);
    const depositsPageSize = embedded ? 6 : 20;
    const [compInvoices, setCompInvoices] = useState([]);
    const [compInvoice, setCompInvoice] = useState(null);
    const [compReason, setCompReason] = useState('');
    // Specific invoice lines to credit (owner, 2026-09-09: "we charged for a
    // bed linen but later found it — pick the item"). The picked
    // line_item_id → quantity; the credit note mirrors exactly those lines
    // and the wallet receives what they come to. 'amount' is the old
    // free-form goodwill mode.
    const [compMode, setCompMode] = useState('amount');
    const [compLines, setCompLines] = useState([]);
    const [compLinesLoading, setCompLinesLoading] = useState(false);
    const [compPicked, setCompPicked] = useState({});
    const [adjusting, setAdjusting] = useState(false);

    const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return DateTime.fromISO(dateString, { zone: 'Asia/Dubai' }).toFormat('dd MMM yy, HH:mm');
    };

    const loadApplyPreview = useCallback(async (amount) => {
        setApplyLoading(true); setApplyError('');
        try {
            const res = await axios.get(`/api/wallet/clients/${clientId}/apply-wallet/preview`, {
                params: amount != null && amount !== '' ? { amount } : {},
            });
            setApplyPreview(res.data);
            if (amount == null || amount === '') setApplyAmount(res.data.amount ? String(res.data.amount.toFixed(2)) : '');
        } catch (err) {
            setApplyError(err.response?.data?.error || 'Could not load the pending invoices.');
        } finally {
            setApplyLoading(false);
        }
    }, [clientId]);

    const openApply = () => { setApplyOpen(true); setApplyPreview(null); setApplyAmount(''); loadApplyPreview(null); };
    const closeApply = () => { if (!applyBusy) { setApplyOpen(false); setApplyError(''); } };

    // Re-plan when the amount is edited (debounced): the list of invoices
    // that will be paid changes with it.
    useEffect(() => {
        if (!applyOpen || applyAmount === '') return undefined;
        const t = setTimeout(() => loadApplyPreview(applyAmount), 350);
        return () => clearTimeout(t);
    }, [applyAmount, applyOpen, loadApplyPreview]);

    const submitApply = async () => {
        const amt = Number(applyAmount);
        if (!(amt > 0)) { setApplyError('Enter the amount to apply.'); return; }
        setApplyBusy(true); setApplyError('');
        try {
            const res = await axios.post(`/api/wallet/clients/${clientId}/apply-wallet`, { amount: amt });
            toast.success(`${money(res.data.applied)} applied to the client's invoices`
                + (Number(res.data.remaining_pending) > 0 ? ` · ${money(res.data.remaining_pending)} still pending` : ' · nothing left pending'));
            setApplyOpen(false);
            // Money moved on BOTH sides — re-read the wallet, the ledger AND the
            // wallet↔Zoho card (it used to keep the pre-apply figure, which read
            // as a failed apply; owner 2026-09-23).
            fetchWallet();
            fetchTransactions(1);
            fetchZoho();
            if (typeof onInvoicesChanged === 'function') onInvoicesChanged();
        } catch (err) {
            setApplyError(err.response?.data?.error || 'Could not apply the wallet.');
        } finally {
            setApplyBusy(false);
        }
    };

    // Wallet ↔ Zoho picture (owner, 2026-09-15): the client's unused Zoho
    // credit, what of it is already spent on uninvoiced bookings, and whether
    // the wallet agrees. Read after the wallet, never blocks the page.
    const [zoho, setZoho] = useState(null);
    const [zohoState, setZohoState] = useState('loading'); // loading | ok | error
    const [ackBusy, setAckBusy] = useState(false);
    const setZohoAck = async (on) => {
        let note = null;
        if (on) {
            note = window.prompt('Why is this difference known and left as it is? (optional)', '') ;
            if (note === null) return;
        }
        setAckBusy(true);
        try {
            await axios.put(`/api/wallet/clients/${clientId}/zoho/acknowledge`, on ? { acknowledged: true, note: note || null } : { acknowledged: false });
            toast.success(on ? 'Difference acknowledged — no daily alert while it stays at this figure' : 'Alerts are back on for this client');
            fetchZoho();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not save that');
        } finally {
            setAckBusy(false);
        }
    };

    const fetchZoho = useCallback(async () => {
        setZohoState('loading');
        try {
            const res = await axios.get(`/api/wallet/clients/${clientId}/zoho`);
            setZoho(res.data);
            setZohoState('ok');
        } catch (err) {
            setZoho(null);
            setZohoState('error');
        }
    }, [clientId]);

    // Deleting an entry added by mistake (owner, 2026-09-18) — managers only.
    // `deleteTarget` opens the confirm dialog; `deletions` is the audit trail
    // behind the "Deleted" tab, which only exists once something was deleted.
    const canDeleteEntries = localStorage.getItem('userRole') === 'manager';
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletions, setDeletions] = useState([]);
    const fetchDeletions = useCallback(async () => {
        if (localStorage.getItem('userRole') !== 'manager') return;
        try {
            const res = await axios.get(`/api/wallet/clients/${clientId}/deletions`);
            setDeletions(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setDeletions([]);
        }
    }, [clientId]);
    // A top-up's own ledger row, or an adjustment made from the Manual
    // Adjustment form. Card top-ups are gateway money and are refunded, never
    // deleted; every other row belongs to a booking, settlement or payout.
    const isDeletableTxn = (t) => (
        (t.type === 'deposit' && t.deposit_id && t.deposit_method !== 'card')
        || (t.type === 'adjustment' && t.origin === 'manual_adjustment')
    );
    const isDeletableDeposit = (d) => d.payment_method !== 'card' && (d.status === 'approved' || d.status === 'rejected');

    // `silent`: re-read without swapping the panel for its skeleton — a
    // deletion happens deep in the ledger, and collapsing the section would
    // throw the page's scroll position away.
    const fetchWallet = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            const res = await axios.get(`/api/wallet/clients/${clientId}`);
            setClient(res.data.client);
            setWallet(res.data.wallet);
            setPending(res.data.pending || null);
        } catch (err) {
            console.error('Error fetching client wallet:', err);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    const fetchTransactions = useCallback(async (page = 1) => {
        try {
            setTransactionsLoading(true);
            // Embedded in Client Details the ledger is one section among many,
            // so it pages at 6; the standalone view keeps its 20.
            const res = await axios.get(`/api/wallet/clients/${clientId}/transactions?page=${page}&limit=${embedded ? 6 : 20}`);
            setTransactions(res.data.transactions);
            setTransactionsTotal(res.data.total);
            setTransactionsPage(page);
        } catch (err) {
            console.error('Error fetching transactions:', err);
        } finally {
            setTransactionsLoading(false);
        }
    }, [clientId, embedded]);

    const fetchDeposits = useCallback(async () => {
        try {
            setDepositsLoading(true);
            const res = await axios.get(`/api/wallet/clients/${clientId}/deposits`);
            setDeposits(res.data);
        } catch (err) {
            console.error('Error fetching deposits:', err);
        } finally {
            setDepositsLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        fetchWallet();
        fetchZoho();
        fetchTransactions(1);
        fetchDeposits();
        fetchDeletions();
    }, [fetchWallet, fetchTransactions, fetchDeposits, fetchZoho, fetchDeletions, refreshToken]);

    useEffect(() => {
        if (adjustDirection !== 'compensation' || compInvoices.length) return;
        (async () => {
            try {
                const res = await axios.get(`/api/wallet/clients/${clientId}/compensable-invoices`);
                setCompInvoices(res.data || []);
            } catch (e) {
                console.error('Error fetching compensable invoices:', e);
                toast.error('Could not load the client\u2019s invoices');
            }
        })();
    }, [adjustDirection, clientId, compInvoices.length]);

    // Derived, not state: recomputed whenever the list changes. The page is
    // clamped so a refresh that shrinks the list can't strand the view on an
    // empty page.
    const depositsTotalPages = Math.max(1, Math.ceil(deposits.length / depositsPageSize));
    useEffect(() => {
        if (depositsPage > depositsTotalPages) setDepositsPage(depositsTotalPages);
    }, [depositsPage, depositsTotalPages]);

    useEffect(() => {
        setCompLines([]);
        setCompPicked({});
        if (!compInvoice) return undefined;
        let alive = true;
        setCompLinesLoading(true);
        axios.get(`/api/wallet/clients/${clientId}/invoices/${compInvoice.value}/lines`)
            .then((res) => { if (alive) setCompLines(res.data?.lines || []); })
            .catch((e) => {
                console.error('Error fetching invoice lines:', e);
                if (alive) toast.error(e.response?.data?.error || 'Could not load the invoice items');
            })
            .finally(() => { if (alive) setCompLinesLoading(false); });
        return () => { alive = false; };
    }, [compInvoice, clientId]);

    const compPickedTotal = Math.round(compLines.reduce(
        (sum, l) => sum + (compPicked[l.line_item_id] > 0 ? l.unit_gross * compPicked[l.line_item_id] : 0), 0) * 100) / 100;
    const togglePick = (l, on) => setCompPicked((p) => {
        const next = { ...p };
        if (on) next[l.line_item_id] = l.quantity; else delete next[l.line_item_id];
        return next;
    });
    const setPickQty = (l, raw) => setCompPicked((p) => {
        const q = Math.min(Math.max(parseFloat(raw) || 0, 0), l.quantity);
        return { ...p, [l.line_item_id]: q };
    });

    const handleCompensate = async () => {
        const byItems = compMode === 'items';
        const picks = byItems
            ? Object.entries(compPicked).filter(([, q]) => q > 0).map(([id, q]) => ({ line_item_id: id, quantity: q }))
            : [];
        const amount = byItems ? compPickedTotal : Math.abs(parseFloat(adjustAmount));
        if (!compInvoice) { toast.error('Select the invoice the compensation relates to'); return; }
        if (byItems && !picks.length) { toast.error('Tick at least one item to credit'); return; }
        if (!byItems && (isNaN(amount) || amount < 1)) { toast.error('Enter an amount of at least AED 1'); return; }
        if (!compReason.trim()) { toast.error('A reason is required \u2014 it appears on the credit note'); return; }
        setAdjusting(true);
        try {
            const res = await axios.post(`/api/wallet/clients/${clientId}/compensate`, {
                invoiceId: compInvoice.value,
                amount: byItems ? undefined : amount,
                lines: byItems ? picks : undefined,
                reason: compReason.trim(),
                description: adjustDescription.trim() || undefined,
            });
            const credited = Number(res.data.credited ?? amount);
            toast.success(`Credited AED ${credited.toFixed(2)} \u2014 credit note ${res.data.creditnote_number} issued`
                + `${byItems ? ` for ${picks.length} item${picks.length === 1 ? '' : 's'}` : ''}`
                + ` (VAT adjusted AED ${Number(res.data.vat_adjusted).toFixed(2)})`);
            setAdjustAmount(''); setAdjustDescription(''); setCompReason(''); setCompInvoice(null); setCompPicked({});
            setAdjustDirection('credit');
            fetchWallet(); fetchTransactions(1); fetchDeposits(); fetchZoho();
            if (typeof onInvoicesChanged === 'function') onInvoicesChanged();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Compensation failed');
        } finally {
            setAdjusting(false);
        }
    };

    const handleAdjust = async () => {
        const amount = Math.abs(parseFloat(adjustAmount));
        if (isNaN(amount) || amount === 0) {
            toast.error('Enter a valid amount');
            return;
        }
        if (!adjustDescription.trim()) {
            toast.error('Description is required');
            return;
        }

        setAdjusting(true);
        try {
            const res = await axios.post(`/api/wallet/clients/${clientId}/adjust`, {
                amount,
                direction: adjustDirection,
                balanceType: adjustBalanceType,
                description: adjustDescription.trim(),
                syncZoho: adjustSyncZoho && adjustBalanceType === 'cash',
                zohoDate: adjustZohoDate || undefined,
            });

            // Show result
            let msg = 'Adjustment applied';
            if (res.data?.zoho) {
                if (res.data.zoho.error) {
                    msg += ` (Zoho sync failed: ${res.data.zoho.error})`;
                    toast.warn(msg);
                } else if (res.data.zoho.type === 'payment') {
                    msg += ' + Zoho payment recorded';
                    toast.success(msg);
                } else if (res.data.zoho.type === 'refund') {
                    msg += ' + Zoho refund recorded';
                    toast.success(msg);
                }
            } else {
                toast.success(msg);
            }

            setAdjustAmount('');
            setAdjustDescription('');
            setAdjustDirection('credit');
            setAdjustBalanceType('cash');
            setAdjustZohoDate(todayStr());
            setAdjustSyncZoho(false);
            fetchWallet();
            fetchTransactions(1);
            fetchZoho();
            if (typeof onInvoicesChanged === 'function') onInvoicesChanged();
        } catch (err) {
            console.error('Error adjusting:', err);
            toast.error(err.response?.data?.error || 'Failed to apply adjustment');
        } finally {
            setAdjusting(false);
        }
    };

    const typeLabel = (t) => {
        if (t.type === 'booking_charge') {
            const desc = t.description || '';
            return (desc.includes('Type:') || desc.includes('Added:') || desc.includes('Removed:') || desc.includes('Amenities') || desc.includes('Time changed'))
                ? 'Booking Change' : 'Booking';
        }
        if (t.type === 'booking_refund') return 'Refund';
        // P1 — deposits are WALLET TOP-UPS and nothing else. Card booking
        // payments are first-class receipts the backend keeps out of the wallet
        // feeds, so a deposit row reaching here is always genuine wallet money.
        // Labelling it "Deposit" invited staff to read a booking card payment as
        // a top-up (and vice versa); the label now names what the row IS.
        if (t.type === 'deposit') return 'Wallet Top-up';
        if (t.type === 'bonus_credit') return 'Bonus Credit';
        if (t.type === 'adjustment') return 'Adjustment';
        return t.type;
    };

    const formatDescription = (t) => {
        let desc = t.description || '';
        desc = desc.replace(/^Booking #(\d+)/, '#$1');
        desc = desc.replace(/\s*\(\d+\.\d+\s*→\s*\d+\.\d+\)/, '');
        desc = desc.replace(/price increase/gi, 'Price Increase');
        desc = desc.replace(/price decrease/gi, 'Price Decrease');
        desc = desc.replace(/price change/gi, 'Price Change');
        return desc.trim();
    };

    const typeChipClass = (type) => {
        switch (type) {
            case 'deposit':
            case 'bonus_credit':
            case 'booking_refund':
                return 'swallet__chip--green';
            case 'booking_charge':
                return 'swallet__chip--red';
            case 'adjustment':
                return 'swallet__chip--orange';
            default:
                return '';
        }
    };

    const depositStatusChip = (status) => {
        switch (status) {
            case 'approved': return 'swallet__chip--green';
            case 'rejected': return 'swallet__chip--red';
            case 'pending': return 'swallet__chip--orange';
            default: return '';
        }
    };

    const depositMethodLabel = (m) => {
        if (m === 'bank_transfer') return 'Bank Transfer';
        if (m === 'online_payment') return 'Online';
        if (m === 'card') return 'Card';
        if (m === 'cash') return 'Cash';
        return m || '—';
    };

    const totalPages = Math.ceil(transactionsTotal / 20);

    if (loading) {
        // Shaped like the real thing (owner, 2026-09-03): balance tiles and a
        // couple of transaction lines, not a bare "Loading...".
        return (
            <div className={embedded ? 'swallet swallet--embedded dp-card' : 'swallet'} aria-busy="true">
                <h3>Wallet</h3>
                <SkeletonBody variant="tiles" tiles={2} rows={2} />
            </div>
        );
    }

    return (
        <div className={embedded ? 'swallet swallet--embedded dp-card' : 'swallet'}>
            {!embedded && (
                <>
                    <Link to="/clients" className="swallet__back">
                        ← Back to Clients
                    </Link>
                    <div className="swallet__header">
                        <h1 className="swallet__title">{client?.display_name} — Wallet</h1>
                    </div>
                </>
            )}
            {embedded && (
                <div className="swallet__embedHead">
                    <h3 className="swallet__embedTitle">{walletLabel || 'Wallet'}</h3>
                    {titleExtra}
                </div>
            )}

            {/* Balance cards */}
            <div className="swallet__balanceRow">
                <div className="swallet__balanceCard">
                    <div className="swallet__balanceLabel">Cash Balance</div>
                    <div className="swallet__balanceValue">{money(wallet?.cash_balance)}</div>
                </div>
                <div className="swallet__balanceCard">
                    <div className="swallet__balanceLabel">Bonus Balance</div>
                    <div className="swallet__balanceValue">{money(wallet?.bonus_balance)}</div>
                </div>
                <div className="swallet__balanceCard swallet__balanceCard--total">
                    <div className="swallet__balanceLabel">Total Available</div>
                    <div className="swallet__balanceValue">{money(wallet?.total_balance)}</div>
                </div>
            </div>

            {/* Client PENDING amounts — what this client still owes / has awaiting
                review (2026-07-21). Same figures the client sees. */}
            {pending && (
                <div className="swallet__pendingRow">
                    <div className="swallet__pendingCard">
                        <div className="swallet__pendingLabel">Pending Invoices</div>
                        <div className="swallet__pendingValue">
                            {pending.invoices_total == null ? '—' : money(pending.invoices_total)}
                        </div>
                        {!pending.zoho_ok && (
                            <div className="swallet__pendingNote">Invoicing system unreachable — may be incomplete</div>
                        )}
                        {/* Pay them from the wallet, from here — managers only,
                            and only when there is both a debt and cash to meet it. */}
                        {localStorage.getItem('userRole') === 'manager'
                            && Number(pending.invoices_total) > 0.009
                            && Number(wallet?.cash_balance) > 0.009 && (
                            <button type="button" className="swallet__btn swallet__btn--primary swallet__pendingAction" onClick={openApply}>
                                Apply wallet to invoices…
                            </button>
                        )}
                    </div>
                    <div className="swallet__pendingCard">
                        <div className="swallet__pendingLabel">Requests Awaiting Review</div>
                        <div className="swallet__pendingValue">{pending.pending_requests_count}</div>
                    </div>
                    {/* Zoho side of the wallet: the unused credit, what of it is
                        already spent on bookings not yet invoiced, and whether the
                        two ledgers agree — the same sum the drift alert rings on. */}
                    <div className={`swallet__pendingCard swallet__zohoCard ${zohoState === 'ok' && zoho?.linked ? (zoho.in_sync ? 'swallet__zohoCard--ok' : 'swallet__zohoCard--off') : ''}`}>
                        <div className="swallet__pendingLabel">Zoho Credit</div>
                        {zohoState === 'loading' ? (
                            <div className="swallet__pendingValue">…</div>
                        ) : zohoState === 'error' ? (
                            <>
                                <div className="swallet__pendingValue">—</div>
                                <div className="swallet__pendingNote">Zoho unreachable right now</div>
                            </>
                        ) : !zoho?.linked ? (
                            <>
                                <div className="swallet__pendingValue">—</div>
                                <div className="swallet__pendingNote">No Zoho contact on this client</div>
                            </>
                        ) : (
                            <>
                                {/* The deduction for bookings already paid from the wallet but not
                                    yet invoiced reads as large as the credit itself (owner, 2026-09-15). */}
                                <div className="swallet__pendingValue swallet__zohoValueRow">
                                    <span>{money(zoho.unused)}</span>
                                    {zoho.uninvoiced?.total > 0.009 && (
                                        <span className="swallet__zohoMinus" title="Already paid from the wallet for bookings that have no invoice yet">
                                            − {money(zoho.uninvoiced.total)}
                                        </span>
                                    )}
                                    {zoho.awaiting?.total > 0.009 && (
                                        <span className="swallet__zohoMinus" title="Paid from the wallet; the invoice exists but Apply & Send has not been pressed yet">
                                            − {money(zoho.awaiting.total)}
                                        </span>
                                    )}
                                    {(zoho.uninvoiced?.total > 0.009 || zoho.awaiting?.total > 0.009) && (
                                        <span className="swallet__zohoEquals">= {money(Math.max(0, zoho.expected))}</span>
                                    )}
                                </div>
                                <div className="swallet__zohoLines">
                                    {zoho.uninvoiced?.total > 0.009 && (
                                        <div className="swallet__zohoLine">
                                            <span>
                                                Uninvoiced booking{zoho.uninvoiced.bookings.length === 1 ? '' : 's'}{' '}
                                                {zoho.uninvoiced.bookings.map((b, i) => (
                                                    <React.Fragment key={b.id}>
                                                        {i > 0 ? ', ' : ''}
                                                        <Link to={`/bookings/${b.id}`}>#{b.id}</Link>
                                                    </React.Fragment>
                                                ))}
                                            </span>
                                            <span>−{money(zoho.uninvoiced.total)}</span>
                                        </div>
                                    )}
                                    {zoho.uninvoiced?.stale_total > 0.009 && (
                                        <div
                                            className="swallet__zohoLine swallet__zohoLine--muted"
                                            title={`Charged from the wallet long ago and never linked to a portal invoice — invoiced another way or test money. Not counted. Bookings: ${zoho.uninvoiced.stale.map((b) => `#${b.id}`).join(', ')}`}
                                        >
                                            <span>
                                                {zoho.uninvoiced.stale.length} older charge{zoho.uninvoiced.stale.length === 1 ? '' : 's'} without an invoice link{' '}
                                                {zoho.uninvoiced.stale.slice(0, 3).map((b, i) => (
                                                    <React.Fragment key={b.id}>
                                                        {i > 0 ? ', ' : ''}
                                                        <Link to={`/bookings/${b.id}`}>#{b.id}</Link>
                                                    </React.Fragment>
                                                ))}
                                                {zoho.uninvoiced.stale.length > 3 ? '…' : ''}
                                                {' '}(not counted)
                                            </span>
                                            <span>{money(zoho.uninvoiced.stale_total)}</span>
                                        </div>
                                    )}
                                    {zoho.awaiting?.total > 0.009 && (
                                        <div className="swallet__zohoLine swallet__zohoLine--action">
                                            <span>
                                                Awaiting Apply &amp; Send on{' '}
                                                {zoho.awaiting.invoices.map((inv, i) => (
                                                    <React.Fragment key={inv.invoice_id}>
                                                        {i > 0 ? ', ' : ''}
                                                        <Link to={`/invoices?open=${inv.invoice_id}`} title="Open the invoice">{inv.invoice_number}</Link>
                                                        {inv.status === 'draft' ? ' (draft)' : ''}
                                                    </React.Fragment>
                                                ))}
                                            </span>
                                            <span>−{money(zoho.awaiting.total)}</span>
                                        </div>
                                    )}
                                    {zoho.entity?.total > 0.009 && (
                                        <div className="swallet__zohoLine swallet__zohoLine--entity" title="Money paid to a billing entity directly. The wallet cannot see or spend it — move it to the main contact, or apply it to that entity's next invoice.">
                                            <span>
                                                Credit on {zoho.entity.entities.map((e) => `${e.label} ${money(e.unused)}`).join(', ')} — not in the wallet
                                            </span>
                                            <span>{money(zoho.entity.total)}</span>
                                        </div>
                                    )}
                                    {zoho.advances > 0.009 && (
                                        <div className="swallet__zohoLine">
                                            <span>Card payments awaiting their invoice</span>
                                            <span>{money(zoho.advances)}</span>
                                        </div>
                                    )}
                                    <div className="swallet__zohoLine">
                                        <span>Should be in wallet</span>
                                        <span>{money(Math.max(0, zoho.expected))}</span>
                                    </div>
                                    {zoho.expected < -0.009 && (
                                        <div className="swallet__zohoLine swallet__zohoDiff is-off">
                                            <span>Charged from the wallet with no Zoho credit behind it</span>
                                            <span>{money(-zoho.expected)}</span>
                                        </div>
                                    )}
                                    <div className={`swallet__zohoLine swallet__zohoDiff ${zoho.in_sync ? 'is-ok' : 'is-off'}`}>
                                        <span>{zoho.in_sync ? 'In sync with the wallet' : 'Difference vs wallet'}</span>
                                        <span>{zoho.in_sync ? '✓' : `${zoho.drift > 0 ? '+' : ''}${money(zoho.drift)}`}</span>
                                    </div>
                                    {!zoho.in_sync && (
                                        <div className="swallet__pendingNote">
                                            {zoho.drift < 0
                                                ? 'Zoho holds credit the wallet does not show — credit the wallet without "Record in Zoho".'
                                                : zoho.expected < -0.009
                                                    ? 'Those bookings will invoice unpaid unless the money reaches Zoho — or the bookings are test data to remove.'
                                                    : 'The wallet holds more than Zoho backs — find the wallet movement that never reached Zoho.'}
                                        </div>
                                    )}
                                    {/* A known difference (owner, 2026-09-22: Permanent Tourist, Hadi): a manager
                                        acknowledges the figure and the daily alert stays quiet until it moves. */}
                                    {!zoho.in_sync && localStorage.getItem('userRole') === 'manager' && (
                                        zoho.acknowledged ? (
                                            <div className="swallet__zohoAck">
                                                <span>
                                                    Known difference — acknowledged {zoho.ack?.by_name ? `by ${zoho.ack.by_name} ` : ''}{formatDate(zoho.ack?.at)}{zoho.ack?.note ? ` · ${zoho.ack.note}` : ''}. No alerts while it stays at this figure.
                                                </span>
                                                <button type="button" className="swallet__zohoAckBtn" onClick={() => setZohoAck(false)} disabled={ackBusy}>Alert again</button>
                                            </div>
                                        ) : (
                                            <div className="swallet__zohoAck">
                                                <span>{zoho.ack ? `Acknowledged at ${money(zoho.ack.drift)} — the figure has moved.` : 'Leave it as it is?'}</span>
                                                <button type="button" className="swallet__zohoAckBtn" onClick={() => setZohoAck(true)} disabled={ackBusy}>
                                                    {ackBusy ? 'Saving…' : 'Acknowledge — no daily alert'}
                                                </button>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {applyOpen && (
                <div className="swallet__overlay" onClick={(e) => { if (e.target === e.currentTarget) closeApply(); }}>
                    <div className="swallet__modal swallet__modal--wide">
                        <h3 className="swallet__modalTitle">Apply wallet to pending invoices</h3>
                        {applyPreview ? (
                            <>
                                <p className="swallet__modalText">
                                    Wallet cash <strong>{money(applyPreview.wallet.usable)}</strong> can be applied ·
                                    pending invoices <strong>{money(applyPreview.pending_total)}</strong>
                                    {applyPreview.pending_count ? ` (${applyPreview.pending_count})` : ''}.
                                    The oldest invoices are paid first; the last one may be paid in part.
                                </p>
                                {Number(applyPreview.held_total) > 0.009 && (
                                    <div className="swallet__applyHeld">
                                        {money(applyPreview.held_total)} of the wallet is held and cannot be applied —{' '}
                                        {applyPreview.held.map((h) => `${money(h.amount)} for ${h.count} ${h.label.toLowerCase()}`).join(', ')}.
                                    </div>
                                )}
                                {(applyPreview.prepaid || []).length > 0 && (
                                    <div className="swallet__applyHeld">
                                        Already paid, not charged again —{' '}
                                        {applyPreview.prepaid.map((p) => `${p.invoice_number} ${money(p.amount)}`
                                            + (p.bookings.length ? ` (booking${p.bookings.length === 1 ? '' : 's'} ${p.bookings.map((b) => `#${b}`).join(', ')})` : '')).join(', ')}.
                                        {' '}Open {applyPreview.prepaid.length === 1 ? 'the invoice' : 'each invoice'} and press Apply Payment to settle {applyPreview.prepaid.length === 1 ? 'it' : 'them'} in Zoho.
                                    </div>
                                )}
                                <div className="swallet__formGroup" style={{ marginBottom: 12 }}>
                                    <label className="swallet__formLabel">Amount to apply (up to {money(applyPreview.max_amount)})</label>
                                    <div className="swallet__applyAmountRow">
                                        <input
                                            type="number"
                                            className="swallet__formInput"
                                            min="0"
                                            max={applyPreview.max_amount}
                                            step="0.01"
                                            value={applyAmount}
                                            onChange={(e) => setApplyAmount(e.target.value)}
                                            disabled={applyBusy}
                                        />
                                        <button type="button" className="swallet__btn" disabled={applyBusy}
                                            onClick={() => setApplyAmount(applyPreview.max_amount.toFixed(2))}>
                                            Use all
                                        </button>
                                    </div>
                                </div>
                                <div className={`swallet__applyPlan${applyLoading ? ' is-loading' : ''}`}>
                                    {applyPreview.plan.length === 0 ? (
                                        <div className="swallet__applyEmpty">Nothing to apply at this amount.</div>
                                    ) : applyPreview.plan.map((row) => (
                                        <div className="swallet__applyRow" key={row.invoice_number}>
                                            <div className="swallet__applyInv">
                                                <strong>{row.invoice_number}</strong>
                                                <span className="swallet__applyMeta">
                                                    {row.date ? DateTime.fromISO(row.date, { zone: 'Asia/Dubai' }).toFormat('d MMM yyyy') : ''}
                                                    {row.entity_label ? ` · ${row.entity_label}` : ''}
                                                </span>
                                            </div>
                                            <div className="swallet__applyAmt">
                                                <strong>{money(row.amount)}</strong>
                                                <span className="swallet__applyMeta">
                                                    {row.paid_in_full ? 'paid in full' : `of ${money(row.balance)} · part`}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="swallet__applyFoot">
                                    Still pending afterwards: <strong>{money(applyPreview.remaining_after)}</strong>
                                </div>
                                <p className="swallet__modalText" style={{ marginTop: 10 }}>
                                    The wallet is debited and the payment is recorded and applied to the invoices in Zoho — exactly what happens when the client pays from their wallet — so their pay link shows the reduced balance straight away. No message is sent to the client.
                                </p>
                            </>
                        ) : (
                            <p className="swallet__modalText">{applyError ? '' : 'Reading the pending invoices…'}</p>
                        )}
                        {applyError && <div className="swallet__applyError">{applyError}</div>}
                        <div className="swallet__modalActions">
                            <button className="swallet__btn" onClick={closeApply} disabled={applyBusy}>Cancel</button>
                            <button
                                className="swallet__btn swallet__btn--success"
                                onClick={submitApply}
                                disabled={applyBusy || applyLoading || !applyPreview || !(Number(applyAmount) > 0) || applyPreview.plan.length === 0}
                            >
                                {applyBusy ? 'Applying…' : `Apply ${Number(applyAmount) > 0 ? money(Number(applyAmount)) : 'wallet'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Adjustment — manager only */}
            {localStorage.getItem('userRole') === 'manager' && <div className="swallet__adjustForm">
                <h3 className="swallet__adjustTitle">Manual Adjustment</h3>

                {/* Direction segmented control */}
                <div className="swallet__segGroup">
                    <button
                        type="button"
                        className={`swallet__segBtn ${adjustDirection === 'credit' ? 'is-active swallet__segBtn--credit' : ''}`}
                        onClick={() => setAdjustDirection('credit')}
                    >
                        Credit (+)
                    </button>
                    <button
                        type="button"
                        className={`swallet__segBtn ${adjustDirection === 'debit' ? 'is-active swallet__segBtn--debit' : ''}`}
                        onClick={() => setAdjustDirection('debit')}
                    >
                        Debit (−)
                    </button>
                    <button
                        type="button"
                        className={`swallet__segBtn ${adjustDirection === 'compensation' ? 'is-active swallet__segBtn--credit' : ''}`}
                        onClick={() => { setAdjustDirection('compensation'); setAdjustBalanceType('cash'); setAdjustSyncZoho(false); }}
                        title="Damage / goodwill credit, papered as a tax credit note on the related invoice"
                    >
                        Compensation
                    </button>
                </div>

                {/* Balance type segmented control */}
                {adjustDirection !== 'compensation' && (
                <div className="swallet__segGroup">
                    <button
                        type="button"
                        className={`swallet__segBtn ${adjustBalanceType === 'cash' ? 'is-active' : ''}`}
                        onClick={() => setAdjustBalanceType('cash')}
                    >
                        Cash Balance
                    </button>
                    <button
                        type="button"
                        className={`swallet__segBtn ${adjustBalanceType === 'bonus' ? 'is-active' : ''}`}
                        onClick={() => { setAdjustBalanceType('bonus'); setAdjustSyncZoho(false); }}
                    >
                        Bonus Balance
                    </button>
                </div>

                )}

                {adjustDirection !== 'compensation' && adjustBalanceType === 'cash' && (
                    <div className="swallet__zohoToggleRow">
                        <label className="swallet__zohoToggle">
                            <input
                                type="checkbox"
                                checked={adjustSyncZoho}
                                onChange={(e) => setAdjustSyncZoho(e.target.checked)}
                            />
                            <span>{adjustDirection === 'credit' ? 'Record in Zoho' : 'Refund in Zoho'}</span>
                        </label>
                        {adjustSyncZoho && (
                            <label className="swallet__zohoDate">
                                <span>{adjustDirection === 'credit' ? 'Payment received' : 'Refund date'}</span>
                                <input
                                    type="date"
                                    value={adjustZohoDate}
                                    max={todayStr()}
                                    onChange={(e) => setAdjustZohoDate(e.target.value)}
                                />
                            </label>
                        )}
                    </div>
                )}

                {adjustDirection === 'compensation' && (
                    <div className="swallet__compFields">
                        <div className="swallet__formGroup swallet__formGroup--wide">
                            <label className="swallet__formLabel">Related invoice *</label>
                            <Select
                                classNamePrefix="swalletsel"
                                options={compInvoices.map((i) => ({
                                    value: i.id,
                                    label: `${i.zoho_invoice_number} — AED ${Number(i.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} — ${i.status} (${String(i.created_on).slice(0, 10)})`,
                                    total: Number(i.total_amount),
                                }))}
                                value={compInvoice}
                                onChange={setCompInvoice}
                                placeholder={compInvoices.length ? 'Pick the invoice the damage relates to…' : 'Loading invoices…'}
                                isClearable
                            />
                            <div className="swallet__formHint">
                                The credit note is issued against this invoice — the compensation cannot exceed its total,
                                and the VAT comes back on this period's return.
                            </div>
                        </div>
                        <div className="swallet__formGroup swallet__formGroup--wide">
                            <label className="swallet__formLabel">Reason *</label>
                            <input
                                type="text"
                                className="swallet__formInput"
                                value={compReason}
                                onChange={(e) => setCompReason(e.target.value)}
                                placeholder="e.g. Damage to wardrobe door during cleaning on 28 Aug"
                                maxLength={150}
                            />
                            <div className="swallet__formHint">Appears on the credit note in Zoho — write it for the accountant.</div>
                        </div>
                        <div className="swallet__formGroup swallet__formGroup--wide swallet__formGroup--lines">
                            <label className="swallet__formLabel">What is credited *</label>
                            <div className="swallet__segGroup swallet__segGroup--small">
                                <button
                                    type="button"
                                    className={`swallet__segBtn ${compMode === 'amount' ? 'is-active swallet__segBtn--credit' : ''}`}
                                    onClick={() => setCompMode('amount')}
                                >
                                    Custom amount
                                </button>
                                <button
                                    type="button"
                                    className={`swallet__segBtn ${compMode === 'items' ? 'is-active swallet__segBtn--credit' : ''}`}
                                    onClick={() => setCompMode('items')}
                                >
                                    Specific items
                                </button>
                            </div>
                            {compMode === 'items' && (
                                !compInvoice ? (
                                    <div className="swallet__formHint">Pick the invoice first — its items appear here.</div>
                                ) : compLinesLoading ? (
                                    /* The table takes shape before it arrives (owner,
                                       2026-09-09) — a one-line hint read like nothing
                                       was coming. */
                                    <div className="swallet__lineTableWrap" aria-busy="true">
                                        <SkeletonBody variant="table" cols={5} rows={3} />
                                        <div className="swallet__formHint">Loading the invoice items…</div>
                                    </div>
                                ) : compLines.length === 0 ? (
                                    <div className="swallet__formHint">This invoice has no items that can be credited — use a custom amount.</div>
                                ) : (
                                    <div className="swallet__lineTableWrap">
                                        <table className="swallet__table swallet__lineTable">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>Item</th>
                                                    <th>Qty</th>
                                                    <th>Each</th>
                                                    <th>Credit</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {compLines.map((l) => {
                                                    const on = compPicked[l.line_item_id] !== undefined;
                                                    const q = on ? compPicked[l.line_item_id] : l.quantity;
                                                    return (
                                                        <tr key={l.line_item_id} className={on ? 'is-picked' : ''}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={on}
                                                                    onChange={(e) => togglePick(l, e.target.checked)}
                                                                    aria-label={`Credit ${l.name}`}
                                                                />
                                                            </td>
                                                            <td>
                                                                <div className="swallet__lineName">{l.name}</div>
                                                                {l.description && l.description !== l.name && (
                                                                    <div className="swallet__lineDesc">{l.description}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="swallet__formInput swallet__lineQty"
                                                                    min="0"
                                                                    max={l.quantity}
                                                                    step="1"
                                                                    value={q}
                                                                    disabled={!on}
                                                                    onChange={(e) => setPickQty(l, e.target.value)}
                                                                    title={`Up to ${l.quantity} on the invoice`}
                                                                />
                                                                <span className="swallet__lineOf">of {l.quantity}</span>
                                                            </td>
                                                            <td className="swallet__lineMoney">AED {l.unit_gross.toFixed(2)}</td>
                                                            <td className="swallet__lineMoney swallet__lineMoney--credit">
                                                                {on ? `AED ${(l.unit_gross * q).toFixed(2)}` : '—'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div className="swallet__formHint">
                                            The credit note lists exactly these items at their invoiced price, VAT included.
                                            {compPickedTotal > 0 && ` Total AED ${compPickedTotal.toFixed(2)} — the wallet receives it in full.`}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}

                <div className="swallet__formRow">
                    <div className="swallet__formGroup">
                        <label className="swallet__formLabel">Amount</label>
                        <div className={`swallet__amountInputWrap ${adjustDirection === 'debit' ? 'is-debit' : 'is-credit'}`}>
                            <span className="swallet__amountSign">{adjustDirection === 'debit' ? '−' : '+'}</span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="swallet__formInput swallet__amountInput"
                                value={adjustDirection === 'compensation' && compMode === 'items'
                                    ? (compPickedTotal > 0 ? compPickedTotal.toFixed(2) : '')
                                    : adjustAmount}
                                readOnly={adjustDirection === 'compensation' && compMode === 'items'}
                                onChange={(e) => setAdjustAmount(Math.abs(parseFloat(e.target.value) || 0) || '')}
                                placeholder={adjustDirection === 'compensation' && compMode === 'items' ? 'from the items' : '0.00'}
                            />
                            <span className="swallet__amountCurrency">AED</span>
                        </div>
                        {/* The amount is GROSS — what the wallet receives. The
                            credit note is tax-inclusive, so Zoho splits it:
                            105 entered = 100.00 + 5.00 VAT. Shown live so
                            nobody has to do the arithmetic. */}
                        {adjustDirection === 'compensation' && compMode === 'amount' && parseFloat(adjustAmount) >= 1 && (
                            <div className="swallet__formHint">
                                = AED {(parseFloat(adjustAmount) / 1.05).toFixed(2)} + AED {(parseFloat(adjustAmount) - parseFloat(adjustAmount) / 1.05).toFixed(2)} VAT
                                (credit note total AED {parseFloat(adjustAmount).toFixed(2)}; the wallet receives the full amount)
                            </div>
                        )}
                    </div>
                    <div className="swallet__formGroup">
                        <label className="swallet__formLabel">
                            {adjustDirection === 'compensation' ? 'Internal note (optional)' : 'Description *'}
                        </label>
                        <input
                            type="text"
                            className="swallet__formInput"
                            value={adjustDescription}
                            onChange={(e) => setAdjustDescription(e.target.value)}
                            placeholder={adjustDirection === 'compensation'
                                ? 'Ledger note — defaults to the credit note reference'
                                : 'Reason for adjustment'}
                        />
                    </div>
                    <button
                        className="swallet__btn swallet__btn--primary"
                        onClick={adjustDirection === 'compensation' ? handleCompensate : handleAdjust}
                        disabled={adjusting}
                        style={{ height: 36 }}
                    >
                        {adjusting ? 'Applying...' : (adjustDirection === 'compensation' ? 'Issue credit note & credit wallet' : 'Apply')}
                    </button>
                </div>

            </div>}

            {/* Saved cards (read-only + admin remove on the client's request).
                Embedded in Client Details they live under Billing Profiles
                instead — a card is a billing identity, not wallet money
                (owner, 2026-09-02). */}
            {!embedded && <ClientSavedCardsSection clientId={clientId} />}

            {/* Tabs */}
            <div className="swallet__tabs">
                <button
                    className={`swallet__tabBtn ${tab === 'transactions' ? 'is-active' : ''}`}
                    onClick={() => setTab('transactions')}
                >
                    Transactions
                </button>
                <button
                    className={`swallet__tabBtn ${tab === 'deposits' ? 'is-active' : ''}`}
                    onClick={() => setTab('deposits')}
                >
                    Top-ups
                </button>
                {canDeleteEntries && deletions.length > 0 && (
                    <button
                        className={`swallet__tabBtn ${tab === 'deleted' ? 'is-active' : ''}`}
                        onClick={() => setTab('deleted')}
                    >
                        Deleted ({deletions.length})
                    </button>
                )}
            </div>

            {/* Transactions */}
            {tab === 'transactions' && (
                <>
                    {transactionsLoading ? (
                        <div className="swallet__empty">Loading...</div>
                    ) : transactions.length === 0 ? (
                        <div className="swallet__empty">No transactions yet.</div>
                    ) : (
                        <>
                            <div className="swallet__txnList">
                                {transactions.map((t) => {
                                    const isBooking = t.reference_id && (t.type === 'booking_charge' || t.type === 'booking_refund');
                                    const amt = parseFloat(t.amount);
                                    const zohoId = t.zoho_id || t.zoho_payment_id;
                                    const isManager = localStorage.getItem('userRole') === 'manager';

                                    return (
                                        <div key={t.id} className="swallet__txnRow">
                                            <div className="swallet__txnMain">
                                                <div className="swallet__txnTop">
                                                    {isBooking ? (
                                                        <Link to={`/bookings/${t.reference_id}`} className={`swallet__chip swallet__chipLink ${typeChipClass(t.type)}`}>
                                                            {typeLabel(t)}
                                                        </Link>
                                                    ) : (
                                                        <span className={`swallet__chip ${typeChipClass(t.type)}`}>
                                                            {typeLabel(t)}
                                                        </span>
                                                    )}
                                                    <span className="swallet__txnDesc">{formatDescription(t)}</span>
                                                    {isManager && zohoId && zohoOrgId && (
                                                        <a
                                                            href={zohoPaymentUrl(zohoOrgId, zohoId)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="swallet__zohoLink"
                                                            title="View in Zoho Books"
                                                        >
                                                            Zoho
                                                        </a>
                                                    )}
                                                </div>
                                                <div className="swallet__txnMeta">
                                                    <span>{formatDate(t.created_at)}</span>
                                                    {t.created_by_name && <><span className="swallet__txnSep">•</span><span>{t.created_by_name}</span></>}
                                                </div>
                                            </div>
                                            <div className="swallet__txnAmounts">
                                                <div className="swallet__txnSplit">
                                                    <span className={parseFloat(t.cash_amount) > 0 ? 'swallet__amount--positive' : parseFloat(t.cash_amount) < 0 ? 'swallet__amount--negative' : ''}>
                                                        {money(Math.abs(parseFloat(t.cash_amount)))}
                                                    </span>
                                                    <span className="swallet__txnSplitLabel">cash</span>
                                                </div>
                                                {parseFloat(t.bonus_amount) !== 0 && (
                                                    <div className="swallet__txnSplit">
                                                        <span className={parseFloat(t.bonus_amount) > 0 ? 'swallet__amount--positive' : 'swallet__amount--negative'}>
                                                            {money(Math.abs(parseFloat(t.bonus_amount)))}
                                                        </span>
                                                        <span className="swallet__txnSplitLabel">bonus</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`swallet__txnTotal ${amt > 0 ? 'swallet__amount--positive' : amt < 0 ? 'swallet__amount--negative' : ''}`}>
                                                {amt > 0 ? '+' : amt < 0 ? '−' : ''}{money(Math.abs(amt))}
                                            </div>
                                            <div className="swallet__txnBalance">
                                                {money(parseFloat(t.cash_balance_after) + parseFloat(t.bonus_balance_after))}
                                            </div>
                                            {canDeleteEntries && (
                                                isDeletableTxn(t) ? (
                                                    <button
                                                        type="button"
                                                        className="wdel__rowBtn"
                                                        title="Delete this entry"
                                                        aria-label="Delete this entry"
                                                        onClick={() => setDeleteTarget({ kind: 'transaction', id: t.id })}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                ) : <span className="wdel__rowBtnSpacer" aria-hidden="true" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="swallet__pagination">
                                    <button
                                        className="swallet__btn"
                                        disabled={transactionsPage <= 1}
                                        onClick={() => fetchTransactions(transactionsPage - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span className="swallet__pageInfo">
                                        Page {transactionsPage} of {totalPages}
                                    </span>
                                    <button
                                        className="swallet__btn"
                                        disabled={transactionsPage >= totalPages}
                                        onClick={() => fetchTransactions(transactionsPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Deposits */}
            {tab === 'deposits' && (
                <>
                    {depositsLoading ? (
                        <div className="swallet__empty">Loading...</div>
                    ) : deposits.length === 0 ? (
                        <div className="swallet__empty">No wallet top-ups yet.</div>
                    ) : (
                        <>
                        <table className="swallet__table">
                            <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Date</th>
                                    <th>Method</th>
                                    <th>Amount</th>
                                    <th>Bonus</th>
                                    <th>Total Credit</th>
                                    <th>Status</th>
                                    <th>Proof</th>
                                    <th>Notes</th>
                                    {localStorage.getItem('userRole') === 'manager' && <th>Zoho</th>}
                                    {canDeleteEntries && <th aria-label="Delete" />}
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.slice((depositsPage - 1) * depositsPageSize, depositsPage * depositsPageSize).map((d) => (
                                    <tr key={d.id}>
                                        <td><span style={REF_CHIP}>TOP-{d.id}</span></td>
                                        <td>{formatDate(d.created_at)}</td>
                                        <td>{depositMethodLabel(d.payment_method)}</td>
                                        <td>{money(d.amount)}</td>
                                        <td>
                                            {parseFloat(d.bonus_amount) > 0
                                                ? `${money(d.bonus_amount)} (${d.bonus_percentage}%)`
                                                : '—'}
                                        </td>
                                        <td style={{ fontWeight: 900 }}>{money(d.total_credit)}</td>
                                        <td>
                                            {/* A pending CARD row is a payment intent awaiting the
                                                gateway — NOT a bank transfer needing manual review
                                                (it is deliberately absent from the Pending Deposits
                                                queue; it settles automatically on capture / rejects
                                                on failure or expiry). */}
                                            {d.payment_method === 'card' && d.status === 'pending' ? (
                                                <span
                                                    className="swallet__chip swallet__chip--blue"
                                                    title="Card payment intent — settles automatically from the gateway (approved on capture, rejected on failure or after 24h). It cannot be reviewed manually."
                                                >
                                                    Card — awaiting payment
                                                </span>
                                            ) : (
                                                <span className={`swallet__chip ${depositStatusChip(d.status)}`}>
                                                    {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {d.proof_of_payment_url ? (
                                                <a href={d.proof_of_payment_url} target="_blank" rel="noopener noreferrer" className="swallet__proofLink">
                                                    View
                                                </a>
                                            ) : '—'}
                                        </td>
                                        <td style={{ color: '#777', fontSize: 12 }}>
                                            {d.admin_notes || d.notes || '—'}
                                        </td>
                                        {localStorage.getItem('userRole') === 'manager' && (
                                            <td>
                                                {d.zoho_payment_id && zohoOrgId ? (
                                                    <a
                                                        href={zohoPaymentUrl(zohoOrgId, d.zoho_payment_id)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="swallet__zohoLink"
                                                    >
                                                        Zoho
                                                    </a>
                                                ) : '—'}
                                            </td>
                                        )}
                                        {canDeleteEntries && (
                                            <td style={{ textAlign: 'right' }}>
                                                {isDeletableDeposit(d) && (
                                                    <button
                                                        type="button"
                                                        className="wdel__rowBtn"
                                                        title="Delete this top-up"
                                                        aria-label="Delete this top-up"
                                                        onClick={() => setDeleteTarget({ kind: 'deposit', id: d.id })}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {depositsTotalPages > 1 && (
                            <div className="swallet__pagination">
                                <button
                                    className="swallet__btn"
                                    disabled={depositsPage <= 1}
                                    onClick={() => setDepositsPage(depositsPage - 1)}
                                >
                                    Previous
                                </button>
                                <span className="swallet__pageInfo">
                                    Page {depositsPage} of {depositsTotalPages}
                                </span>
                                <button
                                    className="swallet__btn"
                                    disabled={depositsPage >= depositsTotalPages}
                                    onClick={() => setDepositsPage(depositsPage + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                        </>
                    )}
                </>
            )}

            {/* Deleted entries — the audit trail (managers only) */}
            {tab === 'deleted' && canDeleteEntries && (
                deletions.length === 0 ? (
                    <div className="swallet__empty">Nothing has been deleted from this wallet.</div>
                ) : (
                    <table className="swallet__table">
                        <thead>
                            <tr>
                                <th>Entry</th>
                                <th>Added</th>
                                <th>Amount</th>
                                <th>Reason</th>
                                <th>Deleted</th>
                                <th>Zoho</th>
                            </tr>
                        </thead>
                        <tbody>
                            {deletions.map((d) => (
                                <tr key={d.id}>
                                    <td>
                                        <span style={REF_CHIP}>{d.kind === 'deposit' ? `TOP-${d.deposit_id}` : `TXN-${(d.transaction_ids || [])[0] ?? ''}`}</span>
                                        {' '}{d.kind === 'deposit' ? `Top-up · ${depositMethodLabel(d.payment_method)}` : 'Manual Adjustment'}
                                    </td>
                                    <td>{formatDate(d.entry_created_at)}</td>
                                    <td style={{ fontWeight: 900, whiteSpace: 'nowrap' }}>
                                        {parseFloat(d.cash_amount) !== 0 && money(d.cash_amount)}
                                        {parseFloat(d.bonus_amount) !== 0 && `${parseFloat(d.cash_amount) !== 0 ? ' + ' : ''}${money(d.bonus_amount)} bonus`}
                                        {parseFloat(d.cash_amount) === 0 && parseFloat(d.bonus_amount) === 0 && '—'}
                                    </td>
                                    <td className="wdel__auditReason">{d.reason}</td>
                                    <td>{formatDate(d.deleted_at)}{d.deleted_by_name ? ` · ${d.deleted_by_name}` : ''}</td>
                                    <td>
                                        {d.zoho_deleted
                                            ? <span className="wdel__auditZoho">Payment #{d.zoho_payment_number} deleted</span>
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )
            )}

            {deleteTarget && (
                <DeleteWalletEntryModal
                    target={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onDeleted={() => {
                        fetchWallet({ silent: true });
                        fetchZoho();
                        fetchTransactions(1);
                        fetchDeposits();
                        fetchDeletions();
                    }}
                />
            )}
        </div>
    );
}
